import { useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import {
  FilterDefinition,
  ModuleId,
  OneDataCollection,
} from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  FormAnswersDataCollectionDocument,
  FormAnswersDataCollectionQuery,
  FormMaterializedAnswersDocument,
  FormResponsesFilterJobRoleNamesDocument,
  FormResponsesFilterJobTitleNamesDocument,
  FormResponsesFilterLegalEntityNamesDocument,
  FormResponsesFilterLocationNamesDocument,
  FormResponsesFilterTeamNamesDocument,
  FormResponsesManagersDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { FormResponsesSidepanel } from 'modules/forms/components/FormResponses/FormRenderResponses/FormResponsesSidepanel'
import { type FormFiltersStorage } from 'modules/forms/hooks/useFormCycleSelection'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import { FilterType } from 'modules/forms/lib/enums'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

import {
  EvaluatorCell,
  FinalResultCell,
  FinalScoreCell,
  ParticipantCell,
  RelatedResourceCell,
  StatusCell,
  TableColumn,
  TableColumnType,
  createQuestionColumns,
} from './columnsRenders/columnsConfig'

interface formResponseFilters extends Record<string, unknown> {
  legalEntityIds?: string[]
  locationIds?: string[]
  managerIds?: string[]
  teamIds?: string[]
  jobRoles?: string[]
  jobTitles?: string[]
  resourceName?: string
  extraSort?: unknown
}

interface Props {
  formId: string
  columns: TableColumnType[]
  filters?: FilterType[]
  relatedResourceLabel?: string
  resourceType: string
  materializedFormResourceType: string
  resourceHeaderModule?: {
    id: ModuleId
    label: string
    href: string
  }
  persistFilters?: boolean
  cycleSelector?: React.ReactNode
  cycleIds?: string[] | null
  headerContent?: (filters: Record<string, unknown>) => React.ReactNode
}

export const FormResponses = ({
  formId,
  columns,
  filters = [],
  relatedResourceLabel = '',
  resourceType,
  materializedFormResourceType,
  resourceHeaderModule,
  persistFilters = false,
  cycleSelector,
  cycleIds,
  headerContent,
}: Props) => {
  const [showExportModal, setShowExportModal] = useState(false)
  const client = useApolloClient()
  const { company } = useCurrentSession()

  // Load form information to check if it's anonymous
  const { form: formInfo } = useLoadForm({ formId, resourceType })

  const [savedStorage, setSavedStorage] = useLocalStorage<FormFiltersStorage | undefined>(
    LOCAL_STORAGE_KEYS.formResponseFilters(formId)
  )
  const baseColumns = useMemo(() => {
    const tableColumns: TableColumn[] = []

    if (columns.includes(TableColumnType.Participant)) {
      tableColumns.push({
        label: i18n.t('forms.responses.columns.participant'),
        render: ParticipantCell,
      })
    }

    if (columns.includes(TableColumnType.Evaluator)) {
      tableColumns.push({
        label: i18n.t('forms.responses.columns.evaluator'),
        render: EvaluatorCell,
      })
    }

    if (columns.includes(TableColumnType.RelatedResource) && relatedResourceLabel) {
      tableColumns.push({
        label: relatedResourceLabel,
        render: RelatedResourceCell,
      })
    }

    if (columns.includes(TableColumnType.Status)) {
      tableColumns.push({
        label: i18n.t('forms.responses.columns.status'),
        render: StatusCell,
        sorting: 'status',
      })
    }

    if (columns.includes(TableColumnType.FinalResult)) {
      tableColumns.push({
        label: i18n.t('forms.responses.columns.finalResult'),
        render: FinalResultCell,
      })
    }

    if (columns.includes(TableColumnType.FinalScore)) {
      tableColumns.push({
        label: i18n.t('forms.responses.columns.finalScore'),
        render: FinalScoreCell,
      })
    }

    return tableColumns
  }, [columns, relatedResourceLabel])
  const filterConfig = useMemo(() => {
    const baseFilters: Record<string, FilterDefinition> = {}

    filters.forEach((filterType) => {
      switch (filterType) {
        case 'relatedResource':
          if (relatedResourceLabel) {
            baseFilters.resourceName = {
              label: relatedResourceLabel,
              type: 'in',
              options: {
                cache: true,
                options: async () => {
                  const result = await client.query({
                    query: FormMaterializedAnswersDocument,
                    variables: {
                      formId,
                      resourceType,
                      materializedFormResourceType,
                    },
                    fetchPolicy: 'cache-first',
                  })
                  const materializedForms =
                    result.data?.forms?.formsConnection?.nodes?.[0]?.materializedForms || []
                  const filtered = materializedForms.filter(
                    (form: { resourceName?: string | null }) => {
                      return Boolean(form && form.resourceName)
                    }
                  )
                  // Map to the format expected by the filter system
                  return filtered.map((form: { id?: string; resourceName?: string | null }) => ({
                    label: form.resourceName || '',
                    value: form.id || '', // Use the materialized form ID, not the resource name
                  }))
                },
              },
            }
          }
          break

        case 'teamIds':
          baseFilters.teamIds = {
            label: i18n.t('forms.responses.filters.team'),
            type: 'in',
            options: {
              cache: true,
              options: async () => {
                const result = await client.query({
                  query: FormResponsesFilterTeamNamesDocument,
                  fetchPolicy: 'cache-first',
                })
                return (result.data?.teams?.teamsConnection?.nodes || []).map((team) => ({
                  label: team.name || '',
                  value: team.id,
                }))
              },
            },
          }
          break

        case 'locationIds':
          baseFilters.locationIds = {
            label: i18n.t('forms.responses.filters.workplace'),
            type: 'in',
            options: {
              cache: true,
              options: async () => {
                const result = await client.query({
                  query: FormResponsesFilterLocationNamesDocument,
                  fetchPolicy: 'cache-first',
                })
                return (result.data?.locations?.locationsConnection?.nodes || []).map(
                  (location) => ({
                    label: location.name || '',
                    value: location.id,
                  })
                )
              },
            },
          }
          break

        case 'legalEntityIds':
          baseFilters.legalEntityIds = {
            label: i18n.t('forms.responses.filters.legalEntity'),
            type: 'in',
            options: {
              cache: true,
              options: async () => {
                const result = await client.query({
                  query: FormResponsesFilterLegalEntityNamesDocument,
                  fetchPolicy: 'cache-first',
                })
                return (result.data?.companies?.legalEntitiesConnection?.nodes || []).map(
                  (legalEntity) => ({
                    label: legalEntity.legalName || '',
                    value: legalEntity.id,
                  })
                )
              },
            },
          }
          break

        case 'managerIds':
          if (company?.id) {
            baseFilters.managerIds = {
              label: i18n.t('forms.responses.filters.manager'),
              type: 'in',
              options: {
                cache: true,
                options: async () => {
                  const result = await client.query({
                    query: FormResponsesManagersDocument,
                    variables: {
                      companyId: company.id,
                    },
                    fetchPolicy: 'cache-first',
                  })
                  return (result.data?.employees?.employeesConnection?.nodes || []).map(
                    (employee) => ({
                      label: employee.fullName || '',
                      value: employee.id,
                    })
                  )
                },
              },
            }
          }
          break

        case 'jobRoles':
          baseFilters.jobRoles = {
            label: i18n.t('forms.responses.filters.jobRole'),
            type: 'in',
            options: {
              cache: true,
              options: async () => {
                const result = await client.query({
                  query: FormResponsesFilterJobRoleNamesDocument,
                  fetchPolicy: 'cache-first',
                })
                return (result.data?.jobCatalog?.rolesConnection?.nodes || []).map((role) => ({
                  label: role.name || '',
                  value: role.id,
                }))
              },
            },
          }
          break

        case 'jobTitles':
          baseFilters.jobTitles = {
            label: i18n.t('forms.responses.filters.jobTitle'),
            type: 'in',
            options: {
              cache: true,
              options: async () => {
                const result = await client.query({
                  query: FormResponsesFilterJobTitleNamesDocument,
                  fetchPolicy: 'cache-first',
                })
                // Extract unique job titles from contract versions
                const jobTitles = result.data?.contracts?.contractVersionsConnection?.nodes || []
                const uniqueJobTitles = Array.from(
                  new Set(
                    jobTitles
                      .map((contract: { jobTitle?: string | null }) => contract.jobTitle)
                      .filter((title): title is string => Boolean(title))
                  )
                )
                return uniqueJobTitles.map((title) => ({
                  label: title,
                  value: title,
                }))
              },
            },
          }
          break
      }
    })

    return Object.keys(baseFilters).length > 0 ? baseFilters : {}
  }, [
    filters,
    relatedResourceLabel,
    formId,
    resourceType,
    materializedFormResourceType,
    client,
    company,
  ])

  const { data: formAnswersData } = useQuery(FormMaterializedAnswersDocument, {
    variables: {
      formId,
      resourceType: resourceType,
      materializedFormResourceType: materializedFormResourceType,
    },
    skip: !formId,
  })

  const [currentMembershipId, setCurrentMembershipId] = useState<{
    membershipId: string | undefined
  }>()
  const source = useGraphqlDataCollectionSource(
    {
      totalItemSummary: true,
      document: FormAnswersDataCollectionDocument,
      mapData: (data) =>
        data.forms.membershipsConnection.nodes.map((item, index) => ({
          ...item,
          _rowIndex: index,
        })),
      mapPagination: (data) => {
        return {
          total: data.forms.membershipsConnection.totalCount ?? 0,
        }
      },
      // @ts-expect-error - savedStorage.filters type compatibility with FiltersState
      currentFilters: persistFilters ? savedStorage?.filters ?? undefined : undefined,
      mapVariables: ({ filters: activeFilters, sortings, search }) => {
        // Helper function to extract filter IDs
        const extractFilterIds = (filterValue: unknown): number[] | undefined => {
          if (!Array.isArray(filterValue) || filterValue.length === 0) return undefined

          const ids = filterValue
            .map((item) => {
              if (item && typeof item === 'object' && 'value' in item) {
                return Number(item.value)
              }
              return Number(item)
            })
            .filter((id) => !isNaN(id))

          return ids.length > 0 ? ids : undefined
        }

        // Helper function to extract filter strings
        const extractFilterStrings = (filterValue: unknown): string[] | undefined => {
          if (!Array.isArray(filterValue) || filterValue.length === 0) return undefined

          const strings = filterValue
            .map((item) => {
              if (item && typeof item === 'object' && 'value' in item) {
                return String(item.value)
              }
              return String(item)
            })
            .filter((str) => str !== '')

          return strings.length > 0 ? strings : undefined
        }

        // Save filters to localStorage if persistFilters is enabled
        if (persistFilters) {
          // Type conversion helper to avoid linter error
          const convertToFormFilters = (filterData: typeof activeFilters): formResponseFilters =>
            filterData

          const storage: FormFiltersStorage = {
            filters: convertToFormFilters(activeFilters),
            cycleId: cycleIds?.[0] || undefined,
          }

          setSavedStorage(storage)
        }

        return {
          formId,
          cycleIds,
          legalEntityIds: extractFilterIds(activeFilters?.legalEntityIds),
          locationIds: extractFilterIds(activeFilters?.locationIds),
          managerIds: extractFilterIds(activeFilters?.managerIds),
          teamIds: extractFilterIds(activeFilters?.teamIds),
          jobRoles: extractFilterIds(activeFilters?.jobRoles),
          jobTitles: extractFilterStrings(activeFilters?.jobTitles),
          materializedFormIds: activeFilters?.resourceName
            ? Array.isArray(activeFilters.resourceName)
              ? activeFilters.resourceName.map((item) => {
                  if (typeof item === 'object' && item !== null && 'value' in item) {
                    return String(item.value)
                  }
                  return String(item)
                })
              : [String(activeFilters.resourceName)]
            : undefined,
          materializedFormResourceType,
          sort: sortings?.[0],
          extraSort: activeFilters?.extraSort,
          search,
        }
      },
      itemOnClick:
        (
          item: NonNullable<
            FormAnswersDataCollectionQuery['forms']['membershipsConnection']['nodes']
          >[0]
        ) =>
        () => {
          setCurrentMembershipId({ membershipId: item.id })
        },
      filters: filterConfig,
      sortings: {
        status: {
          label: i18n.t('forms.responses.columns.status'),
        },
      },
      defaultSortings: {
        field: 'status',
        order: 'asc',
      },
      search: formInfo?.anonymous ? undefined : { enabled: true }, // Hide search button for anonymous forms
      secondaryActions: {
        expanded: 0,
        actions: () => [
          {
            label: i18n.t('common.export'),
            onClick: () => setShowExportModal(true),
            hideLabelWhenExpanded: true,
          },
        ],
      },
    },
    [cycleIds]
  )

  const elements = formAnswersData?.forms?.formsConnection?.nodes?.[0]?.elements ?? []
  const questionColumns = createQuestionColumns(elements)
  const tableColumns = [...baseColumns, ...questionColumns]

  return (
    <>
      {/* Header Content (e.g., ParticipationCard) */}
      {headerContent && (
        <Box paddingX="s16" paddingTop="s16" paddingBottom="s16">
          {headerContent(source.currentFilters)}
        </Box>
      )}

      {/* CSS Grid container to overlay cycle selector */}
      <Box
        style={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gridTemplateColumns: '1fr',
        }}
      >
        <Box style={{ gridRow: '1', gridColumn: '1' }} overflowX="auto">
          <OneDataCollection
            source={source}
            visualizations={[
              {
                type: 'table',
                options: {
                  columns: tableColumns,
                  frozenColumns: 1,
                },
              },
            ]}
          />
        </Box>

        {cycleSelector && (
          <Box
            style={{
              gridRow: '1',
              gridColumn: '1',
              alignSelf: 'start',
              justifySelf: 'end',
              pointerEvents: 'none',
              marginTop: '-4px', // we can remove this, but looks better this way
            }}
            paddingRight="s16"
          >
            <Box style={{ pointerEvents: 'auto' }}>{cycleSelector}</Box>
          </Box>
        )}
      </Box>

      <FormResponsesSidepanel
        membershipId={currentMembershipId?.membershipId ?? ''}
        materializedFormResourceType={materializedFormResourceType}
        formId={formId}
        isOpen={!!currentMembershipId}
        onClose={() => setCurrentMembershipId(undefined)}
        resourceType={resourceType}
        resourceHeaderModule={resourceHeaderModule}
      />
      {showExportModal && (
        <ExportSidepanel
          overrideFilters={{ form_id: [formId] }}
          resource="forms.membership"
          onClose={() => setShowExportModal(false)}
          hasDynamicFields
          metadata={{ form_id: [formId] }}
        />
      )}
    </>
  )
}
